import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from "react";
import { shallowEqual, useSelector } from "react-redux";
import { isEqual, isFunction } from "lodash";
import { initialFilter } from "./DebtCollectionUIHelpers";
import {
  getDebtListScopedFranchiseId,
  isDebtCollectionFranchiseOwner,
  isDebtCollectionSuperAdmin,
} from "./debtCollectionRoles";

const DebtCollectionUIContext = createContext();

/**
 * Returns debt collection list UI state from context (filters, roles, scoped franchise).
 * @returns {object} Context value from {@link DebtCollectionUIProvider}.
 */
export function useDebtCollectionUIContext() {
  return useContext(DebtCollectionUIContext);
}

/**
 * Supplies query params, role flags, and franchise scoping for debt collection list pages.
 * @param {{ children: React.ReactNode }} props - Provider props.
 * @param {React.ReactNode} props.children - Tree to wrap.
 * @returns {JSX.Element} Context provider element.
 */
export function DebtCollectionUIProvider({ children }) {
  const user = useSelector((state) => state.auth.user, shallowEqual);
  const roles = user?.roles;

  const isDebtSuperAdmin = useMemo(() => isDebtCollectionSuperAdmin(roles), [
    roles,
  ]);
  const isDebtFranchiseOwner = useMemo(
    () => isDebtCollectionFranchiseOwner(roles),
    [roles],
  );
  const scopedFranchiseIdForDebtList = useMemo(
    () => getDebtListScopedFranchiseId(user),
    [user],
  );

  const [queryParams, setQueryParamsBase] = useState(initialFilter);
  const setQueryParams = useCallback((nextQueryParams) => {
    setQueryParamsBase((prevQueryParams) => {
      if (isFunction(nextQueryParams)) {
        nextQueryParams = nextQueryParams(prevQueryParams);
      }
      if (isEqual(prevQueryParams, nextQueryParams)) {
        return prevQueryParams;
      }
      return nextQueryParams;
    });
  }, []);

  const value = {
    queryParams,
    setQueryParams,
    isDebtSuperAdmin,
    isDebtFranchiseOwner,
    canManageDebtCollection: isDebtSuperAdmin,
    showDebtFranchiseFilter: isDebtSuperAdmin,
    scopedFranchiseIdForDebtList,
  };

  return (
    <DebtCollectionUIContext.Provider value={value}>
      {children}
    </DebtCollectionUIContext.Provider>
  );
}
